import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { cn } from '../../src/lib/utils';
type DialogProps = {
  open: boolean;
  onClose: () => void;
  labelledBy: string;
  children: React.ReactNode;
  className?: string;
};

/** Accessible modal: Esc / backdrop to close, focus moved in and restored, Tab kept inside, scroll locked. */
export function Dialog({
  open,
  onClose,
  labelledBy,
  children,
  className
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusables = () => Array.from(panelRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])') ?? []);
    requestAnimationFrame(() => (focusables()[0] ?? panelRef.current)?.focus());
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [open, onClose]);
  return createPortal(<AnimatePresence data-id="element-1277" data-mp="3l8r2r6xcijll">
      {open ? <motion.div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0A0A0A]/60 p-4" initial={reduceMotion ? false : {
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={reduceMotion ? undefined : {
      opacity: 0
    }} transition={{
      duration: 0.18
    }} onClick={onClose} data-id="element-1278" data-mp="3gcsb9agq0lgy">
          <motion.div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={labelledBy} tabIndex={-1} initial={reduceMotion ? false : {
        opacity: 0,
        y: 12,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduceMotion ? undefined : {
        opacity: 0,
        y: 8
      }} transition={{
        duration: 0.22,
        ease: [0.22, 1, 0.36, 1]
      }} onClick={event => event.stopPropagation()} className={cn('w-full rounded-2xl border border-hairline bg-panel shadow-xl focus:outline-none', className)} data-id="element-1279" data-mp="2mx6ebsielosf">
            {children}
          </motion.div>
        </motion.div> : null}
    </AnimatePresence>, document.body);
}